import { useState } from 'react'

import {
  FvButton,
  FvLinkButton,
  Icon,
  ManualTrackForm,
  ValidatedForm,
} from '@fv/client-components'
import { getCurrentSequence, getTrackingNumLabel } from '@fv/client-core'
import type { ShipmentLocation } from '@fv/client-types'

import { DividedContent } from '@/components/DividedContent'
import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { UpdateTrackingSequenceForm } from '@/components/list/UpdateTrackingSequenceForm'
import { Pinline } from '@/components/Pinline'
import { apiUri } from '@/constants'

import { usePermissions } from '../auth'
import { HasQuote } from '../auth/HasPermission'
import { DetailedTrackingHistory } from '../track/DetailedTrackingHistory'
import { useTracking } from '../track/queries'
import { useEditTrackingNumber } from '../track/useEditTrackingNumber'
import { useUpdateTrackStatus } from '../track/useUpdateTrackStatus'
import {
  SliderContentBody,
  SliderContentFooter,
  SliderContentHeader,
} from './SliderContentLayout'
import type { ShipmentContentProps } from './types'

type TrackingFormProps = {
  closeForm: () => void
  loadId: string
  trackingNumber?: string
}

const TrackingNumberForm = ({
  closeForm,
  loadId,
  trackingNumber,
}: TrackingFormProps) => {
  const editTrackingNum = useEditTrackingNumber(loadId)

  async function onValidSubmit(form: HTMLFormElement) {
    if (editTrackingNum.isPending) return

    const input = form.elements.namedItem('trackingNumber') as HTMLInputElement
    const value = input.value.trim()

    if (value === trackingNumber) closeForm()
    else editTrackingNum.mutate(value, { onSuccess: closeForm })
  }

  return (
    <ValidatedForm onValidSubmit={onValidSubmit}>
      <InputGroup
        inputType="text"
        className="my-4"
        inputProps={{
          name: 'trackingNumber',
          defaultValue: trackingNumber,
          onChange: undefined,
        }}
        endContent={
          <InputAdornment>
            <FvButton
              theme="default"
              icon={editTrackingNum.isPending ? 'spinner' : 'check'}
              type="submit"
            >
              Save
            </FvButton>
          </InputAdornment>
        }
      />
    </ValidatedForm>
  )
}

type TrackingStatusFormProps = {
  closeForm: () => void
  currentSequence: number
  loadId: string
  locations: ShipmentLocation[]
}

const TrackingStatusForm = ({
  closeForm,
  currentSequence,
  loadId,
  locations,
}: TrackingStatusFormProps) => {
  const [nextSequence, setNextSequence] = useState('')
  const updateTrackStatus = useUpdateTrackStatus(loadId)
  const trackSequence = nextSequence || String(currentSequence)

  return !nextSequence ? (
    <UpdateTrackingSequenceForm
      currentSequence={Number(trackSequence)}
      locations={locations}
      setSequence={setNextSequence}
    />
  ) : (
    <ManualTrackForm
      closeForm={closeForm}
      initialSequence={Number(nextSequence)}
      loading={updateTrackStatus.isPending}
      locations={locations}
      onSave={updateTrackStatus.mutate}
    />
  )
}

export const TrackingPanel = ({
  load,
  item,
  setActiveSlider,
}: ShipmentContentProps & { item?: { id: string; index: number } }) => {
  const { data } = useTracking(load.loadId)
  const { canBook } = usePermissions()
  const { carrierTracking, loadId, workflow, selectedQuote } = load
  const [showEditPro, setShowEditPro] = useState(false)
  const [showEditTracking, setShowEditTracking] = useState(false)

  const tracking = item
    ? data?.items?.find(i => i.itemId === item.id && i.unitIndex === item.index)
    : data

  const defaultNav = (
    <>
      <FvLinkButton icon="hashtag" onClick={() => setShowEditPro(p => !p)}>
        Add or Edit {getTrackingNumLabel(workflow, selectedQuote)}
      </FvLinkButton>
      <FvLinkButton
        icon="thumbtack"
        transform="down-1"
        onClick={() => {
          setShowEditPro(false)
          setShowEditTracking(p => !p)
        }}
      >
        Update tracking status
      </FvLinkButton>
    </>
  )

  const formNav = (
    <FvLinkButton
      icon="arrow-to-left"
      onClick={() => setShowEditTracking(p => !p)}
    >
      Back
    </FvLinkButton>
  )

  return (
    <>
      <SliderContentBody>
        <SliderContentHeader
          {...load}
          tracking={tracking}
          showBolNumber={false}
          trackingNumLabel={getTrackingNumLabel(workflow, selectedQuote)}
          selectedQuote={selectedQuote}
        />

        {canBook && !item && (
          <DividedContent>
            {!showEditTracking ? defaultNav : formNav}
          </DividedContent>
        )}

        {showEditPro && (
          <TrackingNumberForm
            closeForm={() => setShowEditPro(false)}
            loadId={loadId}
            trackingNumber={tracking?.trackingNumber}
          />
        )}

        {canBook && !item && !showEditPro && <Pinline rule />}
        <div className="flex-1 overflow-auto">
          {showEditTracking ? (
            <TrackingStatusForm
              closeForm={() => setShowEditTracking(false)}
              currentSequence={getCurrentSequence(tracking)}
              loadId={loadId}
              locations={load.locations}
            />
          ) : (
            <DetailedTrackingHistory loadId={loadId} />
          )}
        </div>
      </SliderContentBody>
      {!item && (
        <SliderContentFooter>
          <DividedContent theme="blue" className="!justify-center">
            <HasQuote>
              <FvLinkButton
                onClick={() => setActiveSlider('share')}
                icon="share"
              >
                Share
              </FvLinkButton>
            </HasQuote>

            {!!carrierTracking && (
              <a
                href={`${apiUri}/shipments/${loadId}/track/carrier`}
                rel="noreferrer"
                target="_blank"
              >
                <Icon icon="square-arrow-up-right" />
                <span>External tracking</span>
              </a>
            )}
          </DividedContent>
        </SliderContentFooter>
      )}
    </>
  )
}
